// Shared helpers for medstudent page components.

// "Last, First" → "LF" — used for avatar bubbles. Falls back to '?' for empty/missing names.
export function initials(name) {
  return (name || '')
    .split(',')
    .map(s => s.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
}

// Avatar gradient palette, mirroring backend/design.html. Each patient gets a
// deterministic gradient based on a stable identifier — same patient → same
// color across the sidebar card AND the header bar avatar. Used by both
// PatientCard (sidebar list) and PatientHeaderBar (top-of-page).
//
// Kept in sync with components/table/ResultsTable.js → ROW_AVATAR_GRADIENTS_HEX
// so a patient's table-row gradient matches their avatar circle. Pure-blue
// gradients (cyan→blue, blue→indigo) were removed there to avoid colliding
// with the schedules-table header's royal-blue band; we drop them here too
// so the palettes and hash positions stay aligned.
const AVATAR_GRADIENTS = [
  'bg-gradient-to-br from-pink-400 to-purple-600',   // mockup: ML
  'bg-gradient-to-br from-amber-400 to-orange-600',  // mockup: TK
  'bg-gradient-to-br from-orange-400 to-red-500',    // mockup: SR
  'bg-gradient-to-br from-emerald-500 to-teal-600',
  'bg-gradient-to-br from-indigo-500 to-purple-600',
  'bg-gradient-to-br from-red-500 to-rose-600',
  'bg-gradient-to-br from-teal-500 to-cyan-600',     // replaces cyan→blue
];
const COMPLETED_AVATAR_GRADIENT =
  'bg-gradient-to-br from-slate-400 to-slate-600';   // mockup: JD (completed)

export function pickAvatarGradient(patient, completed = false) {
  if (completed) return COMPLETED_AVATAR_GRADIENT;
  const key = String(
    patient?.id || patient?.uuid || patient?.EHR_ID || patient?.patient_name || '',
  );
  if (!key) return AVATAR_GRADIENTS[0];
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

// A visit is "Completed" iff its appointment datetime has passed AND it has at
// least one saved recording. Mirrors how PatientPage.js detects audio: a row
// counts as having recordings if EITHER `transcripts` OR `more_audios` is a
// non-empty array. (transcripts = transcribed audio; more_audios = raw audio
// files not yet transcribed. Either one means audio was recorded.) `time_` is
// the canonical column (HH:MM:SS); `appointment_time` is the display fallback.
export function isVisitCompleted(schedule, now = new Date()) {
  if (!schedule) return false;
  const transcripts = schedule.transcripts;
  const moreAudios = schedule.more_audios;
  const hasRecording =
    (Array.isArray(transcripts) && transcripts.length > 0) ||
    (Array.isArray(moreAudios) && moreAudios.length > 0);
  if (!hasRecording) return false;
  const date = schedule.date_;
  const rawTime = schedule.time_ || schedule.appointment_time;
  if (!date || !rawTime) return false;
  // notes-c.time_ comes back from PostgREST as "HH:MM:SS+00" (Postgres
  // `time with time zone`). V8 treats the bare "+00" suffix as an invalid
  // offset (the spec wants "+0000" or "+00:00") so `new Date(...)` returns
  // Invalid Date and `dt <= now` is always false → the visit never reads as
  // completed. Strip any trailing offset/Z and pad short "HH:MM" forms, then
  // parse as local time (the user thinks of the appointment in clinic-local,
  // not UTC, which matches how the time is displayed in the cards).
  let time = String(rawTime).replace(/[+\-Z].*$/, '');
  if (time.length === 5) time += ':00';
  const dt = new Date(`${date}T${time}`);
  if (Number.isNaN(dt.getTime())) return false;
  return dt <= now;
}
